import React from 'react';
import { ClipboardCheck, Calendar, DollarSign, MessageCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { formatInTimeZone } from 'date-fns-tz';

const BUSINESS_TIMEZONE = 'America/Denver';

const formatAppointment = (iso) =>
  formatInTimeZone(new Date(iso), BUSINESS_TIMEZONE, "EEEE, MMMM d, yyyy 'at' h:mm a (zzz)");

export const StepConfirmation = ({ result, customerEmail, onClose }) => {
  return (
    <div className="text-center space-y-5 py-4" data-testid="booking-confirmation">
      <div className="w-16 h-16 mx-auto rounded-full bg-amber-50 border border-amber-300 flex items-center justify-center">
        <ClipboardCheck className="w-9 h-9 text-amber-600" />
      </div>

      <div>
        <h3 className="text-2xl font-bold text-neutral-950">Booking Request Received!</h3>
        <p className="text-neutral-600 mt-2 max-w-md mx-auto">
          Stefen will reach out shortly to confirm your appointment. Your deposit secures your spot.
        </p>
      </div>

      <div className="bg-neutral-50 border border-neutral-200 rounded-sm p-5 text-left space-y-3 max-w-md mx-auto">
        <div>
          <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">Service</div>
          <div className="text-neutral-900 font-semibold">{result.service_name}</div>
        </div>
        <div className="flex items-start gap-2">
          <Calendar className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
          <div>
            <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">Requested Time</div>
            <div className="text-neutral-900">{formatAppointment(result.start_at)}</div>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <DollarSign className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">Deposit Paid</div>
            <div className="text-neutral-900" data-testid="confirmation-deposit">${(result.amount_paid_cents / 100).toFixed(2)}</div>
            {result.deposit_tax_cents > 0 && (
              <div className="text-neutral-500 text-xs mt-0.5">Includes ${(result.deposit_tax_cents / 100).toFixed(2)} tax ({result.tax_city} {result.tax_rate_percent}%)</div>
            )}
          </div>
        </div>
        {typeof result.balance_due_cents === 'number' && result.balance_due_cents > 0 && (
          <div className="flex items-start gap-2">
            <DollarSign className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">Balance Due at Appointment</div>
              <div className="text-neutral-900" data-testid="confirmation-balance">${(result.balance_due_cents / 100).toFixed(2)}</div>
              <div className="text-neutral-500 text-xs mt-0.5">Includes tax, paid directly to Stefen on arrival (total ${(result.total_with_tax_cents / 100).toFixed(2)})</div>
            </div>
          </div>
        )}
        {result.booking_id && (
          <div className="pt-2 border-t border-neutral-200">
            <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">Reference #</div>
            <div className="text-neutral-700 font-mono text-xs break-all">{result.booking_id}</div>
          </div>
        )}
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 max-w-md mx-auto text-left">
        <div className="flex gap-2 items-start">
          <MessageCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <div className="text-amber-800 font-semibold text-sm mb-1">What happens next</div>
            <p className="text-amber-900/80 text-xs leading-relaxed">
              Stefen will personally text or email <span className="text-neutral-950 font-semibold">{customerEmail}</span> within an hour to confirm your appointment time. If the requested slot isn&apos;t available, he&apos;ll suggest alternatives or refund your deposit.
            </p>
            {result.confirmation_sent && (
              <p className="text-amber-900/80 text-xs leading-relaxed mt-2" data-testid="confirmation-receipt-sent">
                A receipt with your add-ons, deposit, tax and balance due was emailed to you.
              </p>
            )}
          </div>
        </div>
      </div>

      <Button onClick={onClose} className="bg-red-600 hover:bg-red-700" data-testid="confirmation-close-btn">
        Done
      </Button>
    </div>
  );
};
